/**
 * MobileEvents Component - Mobile-optimized events browsing
 * 
 * Provides a streamlined mobile experience for browsing events with:
 * - Touch-friendly event cards
 * - Simple filtering (upcoming/past/all)
 * - Search functionality
 * - Responsive pagination
 * - Navigation to dedicated detail pages
 * - Persistent image caching that survives navigation
 */

import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import MobileLayout from './MobileLayout';
import apiHelper from '../../utils/apiHelper';
import imageCache from '../../utils/imageCache';
import './MobileEvents.css';

const { FILE_NAMES } = require('../../utils/constants');

function MobileEvents() {
  const navigate = useNavigate();
  const [eventsData, setEventsData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [eventImages, setEventImages] = useState({});

  const itemsPerPage = 6;

  // Memoized processed events data with caching optimization
  const processedEvents = useMemo(() => {
    if (eventsData.length === 0) return [];
    
    // Process events with robust date parsing
    const processed = eventsData.map(event => {
      // Handle date parsing
      let date;
      try {
        const rawDate = new Date(event.EventDate);
        if (isNaN(rawDate.getTime())) {
          date = rawDate;
        } else {
          const hour = rawDate.getUTCHours();
          const shiftDay = hour >= 12 ? 1 : 0;
          date = new Date(
            Date.UTC(
              rawDate.getUTCFullYear(),
              rawDate.getUTCMonth(),
              rawDate.getUTCDate() + shiftDay,
              3
            )
          );
        }
      } catch (error) {
        date = new Date(event.EventDate);
      }
      
      event.EventDate = date;
      
      // Convert Topics from comma-separated string to array
      if (event.Topics && typeof event.Topics === 'string') {
        event.Topics = event.Topics.split(',').map(topic => topic.trim()).filter(topic => topic);
      }
      
      return event;
    });

    // Sort events by date (most recent first)
    processed.sort((a, b) => new Date(b.EventDate) - new Date(a.EventDate));
    
    return processed;
  }, [eventsData]);

  // Memoized filtered events with search and date filtering
  const filteredEvents = useMemo(() => {
    let filtered = [...processedEvents];
    const now = new Date();

    // Only show upcoming events
    filtered = filtered.filter(event => {
      if (event.EventDate) {
        const eventDate = new Date(event.EventDate);
        return eventDate > now;
      }
      return false;
    });

    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      filtered = filtered.filter(event => {
        const searchableFields = [
          event.Title,
          event.EventDescription_x002f_Narrative,
          event.EventLocation,
          event.EventCategory,
          event.InPerson_x002f_Virtual,
          event.Speakers,
          ...(event.Topics || [])
        ];
        
        return searchableFields.some(field => 
          field && field.toString().toLowerCase().includes(query)
        );
      });
    }

    // Sort upcoming events by earliest date first
    filtered.sort((a, b) => {
      const dateA = new Date(a.EventDate);
      const dateB = new Date(b.EventDate);
      return dateA - dateB;
    });

    return filtered;
  }, [processedEvents, searchQuery]);

  // Memoized pagination data
  const paginationData = useMemo(() => {
    const totalPages = Math.ceil(filteredEvents.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const currentEvents = filteredEvents.slice(startIndex, startIndex + itemsPerPage);
    
    return {
      totalPages,
      currentEvents,
      hasMore: currentPage < totalPages
    };
  }, [filteredEvents, currentPage, itemsPerPage]);

  // Load event image using persistent cache
  const loadEventImage = async (event) => {
    if (!event.ImageLink) {
      return '/images/news-reading.jpg';
    }

    const imageKey = `event-${event.ID}`;
    
    // Check if already cached (instant return)
    const cachedImage = imageCache.getCachedImage(imageKey);
    if (cachedImage) {
      return cachedImage;
    }

    // Define the loading function for this specific image
    const loadFunction = async () => {
      try {
        // Handle different image source types
        if (event.ImageLink.startsWith('blob:') || event.ImageLink.includes('https://') || 
            event.ImageLink.includes('assets/img/') || event.ImageLink.startsWith('/images/')) {
          return event.ImageLink;
        }

        // Load via API
        const authToken = await apiHelper.getAuthToken();
        const imageUrl = `${process.env.REACT_APP_API_URL}graph/getMediaContent/${event.ImageLink}`;
        
        const response = await fetch(imageUrl, {
          headers: { 'Authorization': `Bearer ${authToken}` }
        });

        if (response.ok) {
          const blob = await response.blob();
          return URL.createObjectURL(blob);
        } else {
          throw new Error('Failed to fetch image');
        }
      } catch (error) {
        console.error('Error loading event image:', error);
        return '/images/news-reading.jpg';
      }
    };

    // Use the persistent cache
    return imageCache.getImage(imageKey, loadFunction);
  };

  // Fetch events data
  useEffect(() => {
    const fetchEvents = async () => {
      try {
        const eventsResult = await apiHelper.fetchJSONFileByNameAndContent(FILE_NAMES.EVENTS);
        setEventsData(eventsResult.fileContent);
        setIsLoading(false);
      } catch (error) {
        console.error('Error fetching events:', error);
        setIsLoading(false);
      }
    };

    fetchEvents();
  }, []);

  // Load images for visible events when filtered events change
  useEffect(() => {
    const loadVisibleImages = async () => {
      const visibleEvents = paginationData.currentEvents;
      const imagePromises = visibleEvents.map(async (event) => {
        if (event.ImageLink) {
          const imageKey = `event-${event.ID}`;
          
          // Skip if already cached or set
          if (eventImages[event.ID] || imageCache.getCachedImage(imageKey)) {
            return;
          }

          try {
            const imageUrl = await loadEventImage(event);
            setEventImages(prev => ({ ...prev, [event.ID]: imageUrl }));
          } catch (error) {
            console.error('Error loading image for event:', event.ID, error);
            setEventImages(prev => ({ ...prev, [event.ID]: '/images/news-reading.jpg' }));
          }
        }
      });

      await Promise.all(imagePromises);
    };

    if (paginationData.currentEvents.length > 0) {
      loadVisibleImages();
    }
  }, [paginationData.currentEvents]);

  // Reset pagination when search query changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery]);

  // Cleanup: Decrement reference counts when component unmounts
  useEffect(() => {
    return () => {
      // Decrement reference counts for all images this component was using
      Object.keys(eventImages).forEach(eventId => {
        const imageKey = `event-${eventId}`;
        imageCache.decrementRefCount(imageKey);
      });
    };
  }, [eventImages]);

  const handleEventClick = (event) => {
    navigate(`/mobile/events/${event.ID}`, { state: { eventData: event } });
  };

  const formatEventDate = (date) => {
    if (!date) return 'Date TBD';
    try {
      const eventDate = new Date(date);
      if (isNaN(eventDate.getTime())) return 'Date TBD';
      
      return eventDate.toLocaleDateString('en-GB', {
        weekday: 'short',
        year: 'numeric',
        month: 'short', 
        day: 'numeric'
      });
    } catch (error) {
      return 'Date TBD';
    }
  };

  const formatEventTime = (startTime, endTime) => {
    if (!startTime && !endTime) return 'Time TBD';
    if (startTime === 'TBC' || endTime === 'TBC') return 'TBC';
    
    try {
      let timeStr = '';
      if (startTime) {
        const [h, m] = startTime.split(':');
        const hour = parseInt(h) % 12 || 12;
        const ampm = parseInt(h) >= 12 ? 'pm' : 'am';
        timeStr += `${hour}:${m} ${ampm}`;
      }
      if (endTime && startTime) {
        const [h, m] = endTime.split(':');
        const hour = parseInt(h) % 12 || 12;
        const ampm = parseInt(h) >= 12 ? 'pm' : 'am';
        timeStr += ` - ${hour}:${m} ${ampm}`;
      }
      return timeStr || 'Time TBD';
    } catch (error) {
      return 'Time TBD';
    }
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  if (isLoading) {
    return (
      <MobileLayout title="Upcoming Events" showBackButton={true} onBackClick={() => navigate('/mobile')} backButtonIcon="home">
        <div className="mobile-loading">
          <div className="mobile-loading-spinner">
            <i className="fas fa-spinner fa-spin"></i>
          </div>
          <div className="mobile-loading-text">Loading events...</div>
        </div>
      </MobileLayout>
    );
  }

  return (
    <MobileLayout title="Upcoming Events" showBackButton={true} onBackClick={() => navigate('/mobile')} backButtonIcon="home">
      <div className="mobile-events">
        {/* Search */}
        <div className="mobile-search-container">
          <div className="mobile-search-box">
            <i className="fas fa-search mobile-search-icon"></i>
            <input
              type="text"
              placeholder="Search events..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="mobile-search-input"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="mobile-search-clear"
              >
                <i className="fas fa-times"></i>
              </button>
            )}
          </div>
        </div>

        {/* Results Info */}
        {searchQuery && (
          <div className="mobile-events-info">
            <span className="mobile-events-count">
              {filteredEvents.length} upcoming event{filteredEvents.length !== 1 ? 's' : ''} found
            </span>
            <span className="mobile-search-info">
              for "{searchQuery}"
            </span>
          </div>
        )}

        {/* Events List */}
        <div className="mobile-events-list">
          {paginationData.currentEvents.length > 0 ? (
            paginationData.currentEvents.map(event => (
              <div 
                key={event.ID} 
                className="mobile-event-card"
                onClick={() => handleEventClick(event)}
              >
                <div className="mobile-event-image">
                  <img
                    src={eventImages[event.ID] || '/images/news-reading.jpg'}
                    alt={event.Title}
                    onError={(e) => {
                      e.target.src = '/images/news-reading.jpg';
                    }}
                  />
                  
                  {/* Media indicators */}
                  {(event.VideoCollateral || event.AudioCollateral || event.FileCollateral) && (
                    <div className="mobile-event-media-indicators">
                      {event.VideoCollateral && <i className="fas fa-video" title="Video available"></i>}
                      {event.AudioCollateral && <i className="fas fa-microphone" title="Audio available"></i>}
                      {event.FileCollateral && <i className="fas fa-file-pdf" title="Documents available"></i>}
                    </div>
                  )}
                  
                  {/* Event type badge */}
                  {event.InPerson_x002f_Virtual && (
                    <div className="mobile-event-type-badge">
                      {event.InPerson_x002f_Virtual}
                    </div>
                  )}
                </div>
                
                <div className="mobile-event-header">
                  <h3 className="mobile-event-title">{event.Title}</h3>
                  {event.EventCategory && (
                    <span className="mobile-event-category">{event.EventCategory}</span>
                  )}
                </div>

                <div className="mobile-event-content">
                  <div className="mobile-event-meta">
                    <div className="mobile-event-date">
                      <i className="fas fa-calendar"></i>
                      <span>{formatEventDate(event.EventDate)}</span>
                    </div>
                    
                    <div className="mobile-event-time">
                      <i className="fas fa-clock"></i>
                      <span>{formatEventTime(event.StartTime, event.EndTime)}</span>
                    </div>
                    
                    {event.EventLocation && (
                      <div className="mobile-event-location">
                        <i className="fas fa-map-marker-alt"></i>
                        <span>{event.EventLocation}</span>
                      </div>
                    )}
                  </div>
                  
                  {event.EventDescription_x002f_Narrative && (
                    <div className="mobile-event-description">
                      {event.EventDescription_x002f_Narrative.length > 120 
                        ? `${event.EventDescription_x002f_Narrative.substring(0, 120)}...`
                        : event.EventDescription_x002f_Narrative
                      }
                    </div>
                  )}
                  
                  {event.Topics && event.Topics.length > 0 && (
                    <div className="mobile-event-topics">
                      {event.Topics.slice(0, 3).map((topic, index) => (
                        <span key={index} className="mobile-event-topic">
                          {topic}
                        </span>
                      ))}
                      {event.Topics.length > 3 && (
                        <span className="mobile-event-topic-more">
                          +{event.Topics.length - 3} more
                        </span>
                      )}
                    </div>
                  )}
                  
                  {event.RegistrationLink && (
                    <div className="mobile-event-actions">
                      <button 
                        className="mobile-event-register-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          window.open(event.RegistrationLink, '_blank');
                        }}
                      >
                        <i className="fas fa-user-plus"></i>
                        Register
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))
          ) : (
            <div className="mobile-no-events">
              <div className="mobile-no-events-icon">
                <i className="fas fa-calendar-times"></i>
              </div>
              <div className="mobile-no-events-text">
                {searchQuery ? `No upcoming events found for "${searchQuery}"` : 'No upcoming events found'}
              </div>
              {searchQuery && (
                <button 
                  className="mobile-clear-search-btn"
                  onClick={() => setSearchQuery('')}
                >
                  Clear Search
                </button>
              )}
            </div>
          )}
        </div>

        {/* Pagination */}
        {paginationData.totalPages > 1 && (
          <div className="mobile-pagination">
            <button 
              className="mobile-page-btn mobile-page-prev"
              onClick={() => handlePageChange(Math.max(currentPage - 1, 1))}
              disabled={currentPage === 1}
            >
              <i className="fas fa-chevron-left"></i>
              Previous
            </button>
            
            <div className="mobile-page-info">
              Page {currentPage} of {paginationData.totalPages}
            </div>
            
            <button 
              className="mobile-page-btn mobile-page-next"
              onClick={() => handlePageChange(Math.min(currentPage + 1, paginationData.totalPages))}
              disabled={currentPage === paginationData.totalPages}
            >
              Next
              <i className="fas fa-chevron-right"></i>
            </button>
          </div>
        )}
      </div>
    </MobileLayout>
  );
}

export default MobileEvents; 